import React, { memo, useCallback, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import Slider from "react-slick";
import { toast } from "react-toastify";

import {
  FiArrowLeft,
  FiArrowRight,
  FiCheck,
  FiClock,
  FiHeart,
  FiLock,
  FiMapPin,
  FiShare2,
  FiUser,
} from "react-icons/fi";
import { FaBath, FaBed, FaUtensils, FaWifi } from "react-icons/fa";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

/* ------------------------------------------------------------------ */
/* Config                                                              */
/* ------------------------------------------------------------------ */
const PRICING_ROUTE = "/pricing";
const FALLBACK_IMAGE = "/image/property-search.jpg";
const OWNER = { name: "Owner", role: "Owner" };

// Replace each `image` with your own file (e.g. "/image/student/sunrise-pg.jpg").
// If an image fails to load, FALLBACK_IMAGE is shown instead.
const LISTINGS = [
  {
    id: 1,
    image: "https://images.unsplash.com/photo-1555854877-bab0e564b8d5?auto=format&fit=crop&w=800&q=70",
    tag: "PG for Boys",
    title: "Sunrise PG",
    price: 8000,
    location: "Near IIT College, Pune",
    room: "Single Room",
    bath: "Attached Bath",
    meals: "Meals Included",
    postedAgo: "5 days ago",
    distance: "2.1 km from you",
  },
  {
    id: 2,
    image: "https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?auto=format&fit=crop&w=800&q=70",
    tag: "Hostel",
    title: "City Hostel",
    price: 6500,
    location: "Near Fergusson College, Pune",
    room: "Shared Room",
    bath: "Common Bath",
    meals: "Meals Included",
    postedAgo: "7 days ago",
    distance: "1.8 km from you",
  },
  {
    id: 3,
    image: "https://images.unsplash.com/photo-1540518614846-7eded433c457?auto=format&fit=crop&w=800&q=70",
    tag: "PG for Girls",
    title: "Comfort Living PG",
    price: 9000,
    location: "Near Symbiosis College, Pune",
    room: "Single Room",
    bath: "Attached Bath",
    meals: "Meals Included",
    postedAgo: "3 days ago",
    distance: "1.2 km from you",
  },
  {
    id: 4,
    image: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=800&q=70",
    tag: "Rental Room",
    title: "Student Rental Room",
    price: 10000,
    location: "Near University Road, Pune",
    room: "Private Room",
    bath: "Attached Bath",
    meals: "No Meals",
    noMeals: true,
    postedAgo: "10 days ago",
    distance: "3.4 km from you",
  },
  {
    id: 5,
    image: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?auto=format&fit=crop&w=800&q=70",
    tag: "PG for Boys",
    title: "Scholars Den PG",
    price: 7500,
    location: "Near COEP, Pune",
    room: "Shared Room",
    bath: "Attached Bath",
    meals: "Meals Included",
    postedAgo: "2 days ago",
    distance: "2.6 km from you",
  },
  {
    id: 6,
    image: "https://images.unsplash.com/photo-1505691938895-1758d7feb511?auto=format&fit=crop&w=800&q=70",
    tag: "Hostel",
    title: "Campus Nest Hostel",
    price: 5800,
    location: "Near MIT WPU, Pune",
    room: "Shared Room",
    bath: "Common Bath",
    meals: "No Meals",
    noMeals: true,
    postedAgo: "6 days ago",
    distance: "4.0 km from you",
  },
];

// Defined once at module level so the slider never receives a new object
const SLIDER_SETTINGS = {
  infinite: true,
  slidesToShow: 3,
  slidesToScroll: 1,
  autoplay: true,
  autoplaySpeed: 3500,
  speed: 600,
  pauseOnHover: true,
  arrows: false,
  cssEase: "ease-in-out",
  responsive: [
    { breakpoint: 1024, settings: { slidesToShow: 2 } },
    { breakpoint: 640, settings: { slidesToShow: 1 } },
  ],
};

const STYLES = `
@keyframes sa-pop { 0% { transform: scale(1); } 40% { transform: scale(1.35); } 100% { transform: scale(1); } }
.sa-pop { animation: sa-pop .45s cubic-bezier(.34,1.56,.64,1); }
.sa-slider .slick-list { padding: 4px 0 18px; }
.sa-slider .slick-track { display: flex; }
.sa-slider .slick-slide { height: inherit; }
.sa-slider .slick-slide > div { height: 100%; }
@media (prefers-reduced-motion: reduce) { .sa-pop { animation: none; } }
`;

const formatPrice = (n) => `₹ ${n.toLocaleString("en-IN")}`;

const handleImageError = (e) => {
  e.currentTarget.onerror = null;
  e.currentTarget.src = FALLBACK_IMAGE;
};

/* ------------------------------------------------------------------ */
/* Small pieces                                                        */
/* ------------------------------------------------------------------ */
const Amenity = memo(function Amenity({ icon, label, struck }) {
  return (
    <div className="flex flex-1 items-center justify-center gap-1.5 px-1">
      <span className="relative shrink-0 text-[#700c28]/70">
        {icon}
        {struck && (
          <span className="absolute left-1/2 top-1/2 h-[1.5px] w-5 -translate-x-1/2 -translate-y-1/2 -rotate-45 bg-[#700c28]/70" />
        )}
      </span>
      <span className="text-[10px] leading-tight text-gray-500">{label}</span>
    </div>
  );
});

const IconButton = ({ label, onClick, children }) => (
  <button
    type="button"
    aria-label={label}
    onClick={onClick}
    className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-gray-800 shadow ring-1 ring-black/5 transition-all duration-200 hover:bg-[#700c28] hover:text-white active:scale-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#700c28]"
  >
    {children}
  </button>
);

/* ------------------------------------------------------------------ */
/* Card (memoized: only re-renders when its own props change)          */
/* ------------------------------------------------------------------ */
const AccommodationCard = memo(function AccommodationCard({
  item,
  isFav,
  onHeart,
  onShare,
  onUnlock,
}) {
  const [pulseKey, setPulseKey] = useState(0);

  const handleHeart = () => {
    setPulseKey((k) => k + 1);
    onHeart(item.id);
  };

  return (
    <div className="h-full px-2.5 py-2">
      <article className="flex h-full flex-col overflow-hidden rounded-2xl bg-white ring-1 ring-black/5 shadow-[0_5px_18px_rgba(17,17,17,0.07)] transition-shadow duration-300 hover:shadow-[0_16px_36px_rgba(112,12,40,0.16)]">
        {/* Image */}
        <div className="relative h-48 bg-gray-100">
          <img
            src={item.image}
            alt={item.title}
            loading="lazy"
            decoding="async"
            onError={handleImageError}
            className="h-full w-full object-cover"
          />

          <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded bg-[#700c28] px-2 py-1 text-[11px] font-semibold text-white shadow">
            <FiCheck size={12} strokeWidth={3} /> Verified
          </span>

          <button
            type="button"
            onClick={handleHeart}
            aria-label={isFav ? "Remove from favourites" : "Add to favourites"}
            aria-pressed={isFav}
            className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-black/45 backdrop-blur transition-transform duration-200 hover:scale-110 active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            <span key={pulseKey} className={`inline-flex ${pulseKey ? "sa-pop" : ""}`}>
              <FiHeart
                size={15}
                className="text-white transition-[fill] duration-300"
                style={{ fill: isFav ? "#fff" : "transparent" }}
              />
            </span>
          </button>

          <span className="absolute bottom-0 left-0 rounded-tr-lg bg-[#700c28] px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide text-white">
            {item.tag}
          </span>
        </div>

        {/* Content */}
        <div className="flex flex-grow flex-col px-4 pb-4 pt-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h4 className="truncate text-base font-bold text-[#1a1a1a]">{item.title}</h4>
              <p className="mt-0.5 flex items-center gap-1 text-xs text-gray-500">
                <FiMapPin size={12} className="shrink-0 text-[#700c28]" />
                <span className="truncate">{item.location}</span>
              </p>
            </div>
            <div className="shrink-0 text-right">
              <p className="whitespace-nowrap text-base font-bold text-[#1a1a1a]">
                {formatPrice(item.price)}
              </p>
              <p className="text-[11px] text-gray-500">/Month</p>
            </div>
          </div>

          <div className="mt-3 flex items-center divide-x divide-gray-200 border-t border-gray-100 pt-3">
            <Amenity icon={<FaBed size={16} />} label={item.room} />
            <Amenity icon={<FaBath size={14} />} label={item.bath} />
            <Amenity icon={<FaWifi size={14} />} label="Wi-Fi" />
            <Amenity icon={<FaUtensils size={13} />} label={item.meals} struck={item.noMeals} />
          </div>

          <div className="mt-3 flex items-center justify-between gap-2 text-[11px] text-gray-500">
            <p className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5">
              <FiClock size={12} className="shrink-0" />
              <span>Posted by {OWNER.role}</span>
              <span aria-hidden="true">•</span>
              <span>{item.postedAgo}</span>
              <span aria-hidden="true">•</span>
              <span className="inline-flex items-center gap-1">
                <FiMapPin size={11} className="text-red-600" />
                {item.distance}
              </span>
            </p>
            <button
              type="button"
              onClick={() => onShare(item)}
              aria-label={`Share ${item.title}`}
              className="shrink-0 rounded-full p-1 text-gray-500 transition-colors hover:text-[#700c28] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#700c28]"
            >
              <FiShare2 size={14} />
            </button>
          </div>

          <div className="mt-auto flex items-center justify-between gap-2 pt-4">
            <div className="flex min-w-0 items-center gap-2.5">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-500">
                <FiUser size={20} />
              </span>
              <p className="flex items-center gap-2 text-sm">
                <span className="font-semibold text-[#1a1a1a]">{OWNER.name}</span>
                <span className="h-4 w-px bg-gray-300" aria-hidden="true" />
                <span className="text-gray-500">{OWNER.role}</span>
              </p>
            </div>

            <button
              type="button"
              onClick={onUnlock}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-[#700c28] px-3.5 py-2.5 text-xs font-semibold text-white shadow-sm transition-all duration-200 hover:bg-[#5a0a20] hover:shadow-md hover:shadow-[#700c28]/30 active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#700c28] focus-visible:ring-offset-2"
            >
              <FiLock size={13} /> Unlock Contact
            </button>
          </div>
        </div>
      </article>
    </div>
  );
});

/* ------------------------------------------------------------------ */
/* Section                                                             */
/* ------------------------------------------------------------------ */
const Student = () => {
  const sliderRef = useRef(null);
  const history = useHistory();
  const [favorites, setFavorites] = useState([]);

  const goToPricing = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    history.push(PRICING_ROUTE);
  }, [history]);

  const toggleFavorite = useCallback((id) => {
    setFavorites((prev) => (prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]));
  }, []);

  const shareListing = useCallback(async (item) => {
    const url = `${window.location.origin}${PRICING_ROUTE}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: item.title, text: `${item.title} – ${item.location}`, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied");
      }
    } catch (e) {
      // share sheet dismissed – nothing to do
    }
  }, []);

  const prev = useCallback(() => sliderRef.current?.slickPrev(), []);
  const next = useCallback(() => sliderRef.current?.slickNext(), []);

  return (
    <>
      <style>{STYLES}</style>

      <section className="bg-white px-4 pb-6 pt-4 font-sans sm:px-8">
        <div className="mx-auto w-full lg:w-[90%]">
          <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-2xl  tracking-tight text-black sm:text-3xl">
                Student Plans
              </h2>
              <p className="mt-1 text-xs text-gray-500 sm:text-sm">
                Find PGs, hostels and rental rooms near your college or preferred location
              </p>
            </div>

            <div className="flex items-center gap-2.5 self-start sm:self-auto">
              <button
                type="button"
                onClick={goToPricing}
                className="rounded-md border border-[#700c28] bg-white px-5 py-2 text-xs font-semibold text-[#700c28] transition-all duration-200 hover:bg-[#700c28] hover:text-white active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#700c28] focus-visible:ring-offset-2"
              >
                View All
              </button>
              <IconButton label="Previous" onClick={prev}>
                <FiArrowLeft size={18} />
              </IconButton>
              <IconButton label="Next" onClick={next}>
                <FiArrowRight size={18} />
              </IconButton>
            </div>
          </div>

          <div className="-mx-2.5">
            <Slider ref={sliderRef} {...SLIDER_SETTINGS} className="sa-slider">
              {LISTINGS.map((item) => (
                <AccommodationCard
                  key={item.id}
                  item={item}
                  isFav={favorites.includes(item.id)}
                  onHeart={toggleFavorite}
                  onShare={shareListing}
                  onUnlock={goToPricing}
                />
              ))}
            </Slider>
          </div>
        </div>
      </section>
    </>
  );
};

export default Student;